import React from 'react';
import { useIntl } from 'react-intl';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';
import Textbox from 'carbon-react/lib/components/textbox';
import Box from 'carbon-react/lib/components/box';

import { Recurrence, RecurrenceEndType, RecurrenceWeekDay } from './types';
import { RecurrenceUnitLabel } from './recurrence-unit-label';
import { WeekDaySelect } from './week-day-select';
import { MonthDaySelect } from './month-day-select';
import { EndSettings } from './end-settings';
import { ScheduleSummary } from './schedule-summary';

interface RecurrenceSettingsProps {
  recurrence: Recurrence;
  recurrenceStartDate: Date;
  recurrenceStartDateError: boolean;
  recurrenceRepeatValue: string;
  recurrenceRepeatValueId?: string;
  recurrenceRepeatValueDisabled?: boolean;
  recurrenceRepeatValueError?: string;
  recurrenceEndType: RecurrenceEndType;
  occurancesValue: string;
  recurrenceEndDate: Date;
  recurrenceEndDateIsBeforeStartDate: boolean;
  recurrenceMonthDay: string;
  recurrenceWeekDay: RecurrenceWeekDay;
  onStartDateChange: (e: DateChangeEvent) => void;
  onRepeatValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRepeatValueBlur?: () => void;
  onEndTypeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOccurancesValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEndDateChange: (e: DateChangeEvent) => void;
  onWeekDayChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onMonthDayChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const RecurrenceSettings = ({
  recurrence,
  recurrenceStartDate,
  recurrenceStartDateError,
  recurrenceRepeatValue,
  recurrenceRepeatValueId,
  recurrenceRepeatValueDisabled,
  recurrenceRepeatValueError,
  recurrenceEndType,
  occurancesValue,
  recurrenceEndDate,
  recurrenceEndDateIsBeforeStartDate,
  recurrenceMonthDay,
  recurrenceWeekDay,
  onStartDateChange,
  onRepeatValueChange,
  onRepeatValueBlur,
  onEndTypeChange,
  onOccurancesValueChange,
  onEndDateChange,
  onWeekDayChange,
  onMonthDayChange
}: RecurrenceSettingsProps) => {
  const intl = useIntl();

  return (
    <div>
      <Box mb="16px">
        <DateInput
          inputHint="DD/MM/YYYY"
          label={intl.formatMessage({ id: 'common.start', defaultMessage: 'Start' })}
          required
          mb="0"
          width="180px"
          value={intl.formatDate(recurrenceStartDate)}
          onChange={onStartDateChange}
          error={
            recurrenceStartDateError
              ? intl.formatMessage({
                  id: 'reporting.reset_start_date_to_today',
                  defaultMessage: 'Reset the start date to today or a future date'
                })
              : ''
          }
        />
      </Box>
      <Box mb="16px" display="flex" alignItems="center">
        <Textbox
          id={recurrenceRepeatValueId}
          label={intl.formatMessage({ id: 'common.every', defaultMessage: 'Every' })}
          required
          disabled={recurrenceRepeatValueDisabled}
          error={recurrenceRepeatValueError}
          value={recurrenceRepeatValue}
          onChange={e => {
            if (!/^-?[0-9]*$/.test(e.target.value)) {return;}
            onRepeatValueChange(e);
          }}
          onBlur={onRepeatValueBlur}
          style={{ width: 48 }}
          mr="8px"
          mb="0px"
          size="medium"
          min={1}
        />
        <Box mt="29px">
          <RecurrenceUnitLabel recurrence={recurrence} />
        </Box>
      </Box>
      {recurrence === Recurrence.WEEKLY && <WeekDaySelect value={recurrenceWeekDay} onChange={onWeekDayChange} />}
      {recurrence === Recurrence.MONTHLY && <MonthDaySelect value={recurrenceMonthDay} onChange={onMonthDayChange} />}
      <EndSettings
        recurrenceEndType={recurrenceEndType}
        occurancesValue={occurancesValue}
        recurrenceEndDate={recurrenceEndDate}
        recurrenceEndDateIsBeforeStartDate={recurrenceEndDateIsBeforeStartDate}
        onEndTypeChange={onEndTypeChange}
        onOccurancesValueChange={onOccurancesValueChange}
        onEndDateChange={onEndDateChange}
      />
      <ScheduleSummary
        recurrence={recurrence}
        recurrenceStartDate={recurrenceStartDate}
        recurrenceRepeatValue={recurrenceRepeatValue}
        recurrenceEndType={recurrenceEndType}
        occurancesValue={occurancesValue}
        recurrenceEndDate={recurrenceEndDate}
        recurrenceWeekDay={recurrenceWeekDay}
        recurrenceMonthDay={recurrenceMonthDay}
      />
    </div>
  );
};
